import { eventInfo } from '../data/footer.js';

export const accountTicketSection = {
  empty: {
    title: 'Билет ещё не куплен',
    description: 'Выберите тариф и оплатите билет — он появится здесь автоматически.',
    cta: { label: 'Выбрать тариф', href: '/tariff' }
  },
  card: {
    kicker: 'Главное для входа',
    sectionTitle: 'Ваш проходной билет',
    sectionTitleEmpty: 'Билет ещё не куплен',
    helpCta: { label: 'Нужна помощь?', href: '#help' },
    tariffLabel: 'Тариф',
    passesLabel: 'Проходов',
    priceLabel: 'Цена',
    statusLabelLabel: 'Статус',
    qrAriaLabel: 'QR-код билета',
    qrHint: 'Нажмите, чтобы скачать QR в PNG',
    memoLabel: 'Памятка',
    memoSteps: [
      'Откройте этот экран перед входом.',
      'Покажите QR-код администратору.',
      'Если связи нет — QR есть в письме после оплаты.'
    ],
    payCta: 'Завершить оплату'
  },
  status: {
    paid: { label: 'Оплачен', tone: 'ok' },
    pending: { label: 'Ожидает оплаты', tone: 'warn' }
  },
  // Тексты бейджа использования билета.
  usage: {
    pendingToken:
      'Билет в обработке. Если оплата прошла больше минуты назад — обновите страницу.',
    allActiveOne: 'Можно проходить',
    allActiveMany: (total) => `Активны ${total} проходов`,
    partial: (used, total) => `Использовано ${used} из ${total}`,
    fullyUsed: 'Билет использован',
    passesLineActive: (total) => `${total} ${pluralRu(total, 'активный проход', 'активных прохода', 'активных проходов')}`,
    passesLinePartial: (used, total) =>
      `${used}/${total} использовано — осталось ${total - used}`,
    qrAlt: (orderId) => `QR-код билета №${orderId}`
  },
  event: {
    title: 'Информация о форуме',
    // Адрес берём из единого источника (footer.js).
    address: eventInfo.address
  }
};

// Простое склонение по русским правилам.
export function pluralRu(n, one, few, many) {
  const abs = Math.abs(Number(n) || 0);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

// IS_PAID = 22 на бэке (см. app/Models/OrderStatus.php в itecho-erp-backend).
export const PAID_STATUS_ID = 22;

const PAID_STATUS_KEYWORDS = ['оплач', 'выполнен', 'paid', 'completed'];

export function isOrderPaid(order) {
  if (order?.status?.id === PAID_STATUS_ID) return true;
  const name = (order?.status?.name ?? '').toLowerCase();
  return PAID_STATUS_KEYWORDS.some((kw) => name.includes(kw));
}

export function ticketTitleFromOrder(order) {
  const product = order?.products?.[0];
  return product?.name ?? product?.nomenclature?.name ?? 'Билет на форум';
}

export function formatPrice(value) {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return '';
  return new Intl.NumberFormat('ru-RU').format(num) + ' ₽';
}

export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Рассчитывает состояние бейджа использования билета.
 */
export function computeTicketUsage(order) {
  if (!isOrderPaid(order)) return null;
  const total = Number(order?.total_tickets);
  const used = Number(order?.redeemed_count);
  const token = order?.redeem_token;
  if (!token || !Number.isFinite(total) || total <= 0) {
    return {
      state: 'pending',
      tone: 'muted',
      text: accountTicketSection.usage.pendingToken,
      passesLine: '—',
      statusLine: 'Готовим QR',
      showQr: false,
      grayedOut: false
    };
  }
  const usedSafe = Number.isFinite(used) && used >= 0 ? used : 0;
  if (usedSafe >= total) {
    return {
      state: 'used',
      tone: 'danger',
      text: accountTicketSection.usage.fullyUsed,
      passesLine: `${total}/${total} использовано`,
      statusLine: 'Использован',
      showQr: true,
      grayedOut: true
    };
  }
  if (usedSafe === 0) {
    return {
      state: 'active',
      tone: 'ok',
      text:
        total === 1
          ? accountTicketSection.usage.allActiveOne
          : accountTicketSection.usage.allActiveMany(total),
      passesLine: accountTicketSection.usage.passesLineActive(total),
      statusLine: 'Можно проходить',
      showQr: true,
      grayedOut: false
    };
  }
  return {
    state: 'partial',
    tone: 'warn',
    text: accountTicketSection.usage.partial(usedSafe, total),
    passesLine: accountTicketSection.usage.passesLinePartial(usedSafe, total),
    statusLine: 'Можно проходить',
    showQr: true,
    grayedOut: false
  };
}
